import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import {
  Container,
  Section,
  SectionHead,
  Card,
  fadeUp,
  stagger,
} from "@comai-portal/design-system";
import { THEMES_PAGE_DATA } from "../generated/themes-page-data";
import { PAGE_HEROES } from "../content/page-copy";
import { fetchThemeLibrary, type LiveThemeEntry } from "../services/themeLibraryLive";

const HERO = PAGE_HEROES["themes"]!;

/**
 * ThemesPage — the theme library, rendered the way /prompts is. (CO 680 Phase 2b)
 *
 * The data module is GENERATED from the house theme supply
 * (scripts/generate-themes-page.ts → themes-page-data.ts): the six house
 * themes with the resolved colors of their four variants, drawn here as
 * swatches with no token resolution in the browser. Everything that can change
 * without a build — status, the last proof, provenance, the themes the house
 * does not ship — comes from ONE tokenless read of `rendering_theme_library`
 * and is laid over the built cards by ref. The build never carries a status, so
 * this page can never claim `proven` on its own: without the read every card
 * reads `untested`, which is also what the library answers for a theme no proof
 * covers.
 *
 * `?tenant=<id>` adds that tenant's library to the same list. The read stays
 * tokenless, so L10 holds: a reader sees only what the tenant has assigned.
 * `?ref=<ref>` shows one card; `?q=` searches title and ref.
 *
 * A reading surface. Nothing here assigns, edits or deletes a theme — the owner
 * does that from their agent (tenant_theme_assign, tenant_theme_put), and the
 * prompt library is where the prompts for it live.
 */

type BuiltTheme = (typeof THEMES_PAGE_DATA.themes)[number];
type VariantLabel = keyof BuiltTheme["swatches"];
type Role = (typeof THEMES_PAGE_DATA.roles)[number];

const BUILT: readonly BuiltTheme[] = THEMES_PAGE_DATA.themes;

/**
 * One authored line per house theme. Page copy, not a system fact: the tokens
 * documents carry engineering notes, not reader text. The page test holds this
 * map and the generated supply to the same set of ids.
 */
export const THEME_BLURBS: Record<string, string> = {
  paper: "Warm off-white ground, indigo primary, teal accent. Light at home.",
  ink: "Near-black ground with the same roles as paper over the other palette. Dark at home.",
  field: "Paper's ground with teal as primary and indigo as accent — the booking page's reading.",
  signal: "Ink's ground with teal as primary and indigo as accent. Dark at home.",
  material: "Material 3 dynamic color from one indigo seed, tonal-spot scheme.",
  glass: "Paper with an additive glass layer on raised surfaces; the layer never carries money.",
};

/** The two home variants a card shows side by side. */
const SHOWN_VARIANTS: readonly VariantLabel[] = ["light/standard", "dark/standard"];

/** Teal for proven, amber for untested — the /prompts treatments, never blurred. */
function statusClasses(status: string): string {
  return status === "proven"
    ? "border-flow/40 bg-flow-soft text-flow dark:border-flow-bright/40 dark:bg-flow/15 dark:text-flow-bright"
    : "border-[#F59E0B]/50 bg-[#FEF3C7] text-[#92400E] dark:border-[#F59E0B]/40 dark:bg-[#F59E0B]/15 dark:text-[#FBBF24]";
}

function StatusBadge({ status }: { status: string }) {
  return (
    <span
      className={`rounded-pill border px-2 py-0.5 text-[11px] font-grotesk font-semibold uppercase tracking-wide ${statusClasses(status)}`}
    >
      {status}
    </span>
  );
}

/** Six role colors of one variant as a strip; the surface carries the rest. */
function Swatch({
  label,
  colors,
}: {
  label: string;
  colors: Record<Role, string>;
}) {
  return (
    <figure className="min-w-0 flex-1" data-testid="swatch" data-variant={label}>
      <div
        className="flex h-14 overflow-hidden rounded-md border border-mute-line dark:border-ink-line"
        style={{ backgroundColor: colors.surface }}
      >
        {THEMES_PAGE_DATA.roles.map((role) => (
          <span
            key={role}
            className="flex-1"
            title={`${role} ${colors[role]}`}
            data-role={role}
            style={{ backgroundColor: colors[role] }}
          />
        ))}
      </div>
      <figcaption className="mt-1 text-[11px] text-mute dark:text-mute-soft">{label}</figcaption>
    </figure>
  );
}

/** A card's data: a built house theme, a live entry, or both joined on ref. */
interface CardData {
  ref: string;
  title: string;
  version: string | null;
  origin: "house" | "market" | "tenant";
  kind: "tokens" | "css";
  glass: boolean;
  blurb: string | null;
  built: BuiltTheme | null;
  live: LiveThemeEntry | null;
  /** True when the live entry describes the same bytes the card was built from. */
  sameVersion: boolean;
}

function proofLine(e: LiveThemeEntry): string {
  const at = e.last_proof?.at ? new Date(e.last_proof.at).toISOString().slice(0, 10) : null;
  const places = e.proven_on.length;
  return `Proven on ${places} ${places === 1 ? "place" : "places"}${at ? ` · ${at}` : ""}`;
}

function ThemeCard({ card }: { card: CardData }) {
  const status = card.live && card.sameVersion ? card.live.status : "untested";
  const live = card.sameVersion ? card.live : null;
  // The design-system Card forwards no data attributes; the ref rides on a plain wrapper.
  return (
    <div className="h-full" data-testid="theme-card" data-ref={card.ref}>
    <Card hover className="h-full flex flex-col">
      <div className="flex flex-wrap items-center gap-2 mb-1.5">
        <h3 className="text-display-sm">{card.title}</h3>
        <StatusBadge status={status} />
        {card.glass && (
          <span className="rounded-pill border border-mute-line px-2 py-0.5 text-[11px] font-grotesk text-mute dark:border-ink-line dark:text-mute-soft">
            glass layer
          </span>
        )}
      </div>
      {card.blurb && <p className="mb-3 text-sm text-ink dark:text-paper">{card.blurb}</p>}

      {card.built && (
        <div className="mb-3 flex gap-3">
          {SHOWN_VARIANTS.map((v) => (
            <Swatch key={v} label={v} colors={card.built!.swatches[v]} />
          ))}
        </div>
      )}

      <div className="mt-auto flex flex-wrap gap-x-4 gap-y-1 text-xs text-mute dark:text-mute-soft">
        <span>
          <span className="font-grotesk font-semibold">Ref </span>
          <code className="font-mono">{card.ref}</code>
        </span>
        <span>
          <span className="font-grotesk font-semibold">Kind </span>
          {card.kind === "tokens" ? "tokens" : "css sheet"}
        </span>
        <span>
          <span className="font-grotesk font-semibold">From </span>
          {card.origin === "house" ? "ComOS" : card.origin === "market" ? "the market" : card.live?.owner_tenant_id ?? "a tenant"}
        </span>
        {live?.base && (
          <span>
            <span className="font-grotesk font-semibold">Base </span>
            <code className="font-mono">{live.base}</code>
          </span>
        )}
        {live && live.assigned.length > 0 && (
          <span>
            <span className="font-grotesk font-semibold">Draws at </span>
            {live.assigned.join(", ")}
          </span>
        )}
      </div>

      {live && live.status === "proven" && (
        <p className="mt-2 text-xs text-flow dark:text-flow-bright" data-testid="proof-line">
          {proofLine(live)}
        </p>
      )}
      {live?.made_by && (
        <p className="mt-2 text-xs text-mute dark:text-mute-soft" data-testid="made-by">
          Made by prompt{" "}
          <Link
            to={`/prompts?q=${encodeURIComponent(live.made_by.prompt_id)}`}
            className="underline decoration-mute-line underline-offset-2 hover:text-ink dark:hover:text-paper"
          >
            {live.made_by.prompt_id}
          </Link>
        </p>
      )}
    </Card>
    </div>
  );
}

/** Join the built supply and the live read on ref: built-only, both, or live-only. */
export function joinCards(built: readonly BuiltTheme[], live: readonly LiveThemeEntry[] | null): CardData[] {
  const byRef = new Map<string, LiveThemeEntry>();
  for (const e of live ?? []) byRef.set(e.ref, e);
  const cards: CardData[] = built.map((b) => {
    const e = byRef.get(b.ref) ?? null;
    byRef.delete(b.ref);
    return {
      ref: b.ref,
      title: b.id,
      version: b.version,
      origin: "house",
      kind: "tokens",
      glass: b.glass,
      blurb: THEME_BLURBS[b.id] ?? null,
      built: b,
      live: e,
      sameVersion: e !== null && e.sha256.startsWith(b.sha16),
    };
  });
  // Whatever the house does not ship: market registrations and a tenant's assigned themes.
  for (const e of byRef.values()) {
    cards.push({
      ref: e.ref,
      title: e.title,
      version: null,
      origin: e.origin,
      kind: e.kind,
      glass: e.glass,
      blurb: null,
      built: null,
      live: e,
      sameVersion: true,
    });
  }
  return cards;
}

export function ThemesPage() {
  const [params, setParams] = useSearchParams();
  const tenant = (params.get("tenant") ?? "").trim().toLowerCase();
  const ref = (params.get("ref") ?? "").trim();
  const q = (params.get("q") ?? "").trim();
  const needle = q.toLowerCase();

  const setControl = (key: string, value: string) => {
    const next = new URLSearchParams(params);
    if (value === "") next.delete(key);
    else next.set(key, value);
    setParams(next, { replace: true });
  };

  // The live read: null until it arrives, and null if it never does.
  const [live, setLive] = useState<LiveThemeEntry[] | null>(null);
  const [readFailed, setReadFailed] = useState(false);
  useEffect(() => {
    let alive = true;
    setLive(null);
    setReadFailed(false);
    fetchThemeLibrary(tenant || undefined)
      .then((entries) => {
        if (alive) setLive(entries);
      })
      .catch(() => {
        // The page stands on its build-time data.
        if (alive) setReadFailed(true);
      });
    return () => {
      alive = false;
    };
  }, [tenant]);

  const all = joinCards(BUILT, live);
  const shown = all.filter(
    (c) =>
      (ref === "" || c.ref === ref) &&
      (needle === "" || c.title.toLowerCase().includes(needle) || c.ref.toLowerCase().includes(needle)),
  );

  return (
    <div className="bg-paper text-ink dark:bg-ink dark:text-paper">
      <Section>
        <Container width="prose" className="text-center">
          <motion.div variants={stagger} initial="hidden" animate="show">
            <motion.p variants={fadeUp} className="ds-eyebrow mb-5">
              {HERO.eyebrow}
            </motion.p>
            <motion.h1
              variants={fadeUp}
              className="font-display font-semibold tracking-[-0.02em] text-[clamp(2rem,5vw,3.5rem)] leading-[1.08] text-balance"
            >
              {HERO.h1}
            </motion.h1>
            <motion.p variants={fadeUp} className="text-lead text-mute dark:text-mute-soft mt-6">
              {HERO.lead}
            </motion.p>
          </motion.div>
        </Container>
      </Section>

      <Section tint="sunk">
        <Container>
          <SectionHead
            eyebrow="The library"
            title="Every theme ComOS holds, with its standing."
            lead="The house themes ship with the system; the market holds what others register; a tenant's own themes appear here once they are assigned somewhere. A theme reads proven only while a stored proof matches the exact bytes being served — change a byte, or the catalog it is drawn with, and it reads untested again until the next proof run."
          />

          <div className="mt-8 flex flex-wrap items-end gap-4">
            <label className="flex flex-col gap-1 text-xs font-grotesk font-semibold text-mute dark:text-mute-soft">
              Search
              <input
                type="search"
                value={q}
                onChange={(e) => setControl("q", e.target.value)}
                placeholder="title or ref"
                className="rounded-md border border-mute-line bg-paper px-3 py-1.5 text-sm font-normal text-ink dark:border-ink-line dark:bg-ink dark:text-paper"
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-grotesk font-semibold text-mute dark:text-mute-soft">
              Tenant
              <input
                type="text"
                value={tenant}
                onChange={(e) => setControl("tenant", e.target.value.trim().toLowerCase())}
                placeholder="a tenant id, e.g. earth"
                className="rounded-md border border-mute-line bg-paper px-3 py-1.5 text-sm font-normal text-ink dark:border-ink-line dark:bg-ink dark:text-paper"
              />
            </label>
            {ref !== "" && (
              <button
                type="button"
                onClick={() => setControl("ref", "")}
                className="rounded-pill border border-mute-line px-3 py-1 text-xs font-grotesk dark:border-ink-line"
              >
                Show all
              </button>
            )}
            <p role="status" className="ml-auto text-xs text-mute dark:text-mute-soft">
              {shown.length} of {all.length} themes
              {live === null && !readFailed ? " · reading the library…" : ""}
              {readFailed ? " · live read unavailable; statuses are the build's" : ""}
            </p>
          </div>

          {shown.length === 0 ? (
            <p className="mt-8 text-sm text-mute dark:text-mute-soft">
              No theme matches. {tenant ? `A tenant's theme appears here only once it is assigned somewhere.` : ""}
            </p>
          ) : (
            <motion.div
              variants={stagger}
              initial="hidden"
              animate="show"
              className="mt-8 grid gap-6 sm:grid-cols-2 lg:grid-cols-3"
            >
              {shown.map((c) => (
                <motion.div key={c.ref} variants={fadeUp}>
                  <ThemeCard card={c} />
                </motion.div>
              ))}
            </motion.div>
          )}
        </Container>
      </Section>

      <Section>
        <Container width="prose">
          <SectionHead
            eyebrow="Acting on a theme"
            title="Your agent changes it; this page reads it."
            lead="A tenant holds up to sixteen named themes, each a house or market base under its own stylesheet. One is the default; any view or page can carry another. The owner assigns with tenant_theme_assign, edits with tenant_theme_put, and reads this same library with rendering_theme_library — from the connector, or from a node they run."
          />
          <p className="mt-6 text-sm text-mute dark:text-mute-soft">
            The{" "}
            <Link to="/prompts?q=theme" className="underline decoration-mute-line underline-offset-2 hover:text-ink dark:hover:text-paper">
              prompt library
            </Link>{" "}
            is where the prompts for changing, editing and comparing themes live, each proven by a journaled run before it is listed.
          </p>
        </Container>
      </Section>
    </div>
  );
}
